/**
 * Date formatting utilities that handle dates consistently across timezones.
 * 
 * League days are stored with their intended date (e.g., "March 28, 2026") and should
 * display the same date regardless of the user's timezone. This prevents issues where
 * a player in a different timezone sees a different date than intended.
 */

/**
 * Formats a league day date in a timezone-agnostic way.
 * Always displays the intended date regardless of the user's local timezone.
 * 
 * @param date - Date object or ISO string
 * @param locale - Locale for formatting (default: 'en-US')
 * @param options - Intl.DateTimeFormat options (default: long format)
 * @returns Formatted date string
 */
export function formatLeagueDayDate(
  date: Date | string,
  locale: string = 'en-US',
  options?: Intl.DateTimeFormatOptions
): string {
  // Convert to Date if string
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Extract the date components using UTC methods to avoid timezone conversion
  const year = dateObj.getUTCFullYear();
  const month = dateObj.getUTCMonth();
  const day = dateObj.getUTCDate();
  
  // Create a new date using local time with the extracted components
  // This ensures the date displays correctly regardless of timezone
  const localDate = new Date(year, month, day);
  
  // Format using Intl.DateTimeFormat
  const defaultOptions: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  
  return localDate.toLocaleDateString(locale, options || defaultOptions);
}

/**
 * Formats a league day date in short format (e.g., "3/28/2026" or "28/3/2026").
 * Always displays the intended date regardless of the user's local timezone.
 * 
 * @param date - Date object or ISO string
 * @param locale - Locale for formatting (default: 'en-GB' for DD/MM/YYYY)
 * @returns Formatted date string
 */
export function formatLeagueDayDateShort(
  date: Date | string,
  locale: string = 'en-GB'
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  const year = dateObj.getUTCFullYear();
  const month = dateObj.getUTCMonth();
  const day = dateObj.getUTCDate();
  
  const localDate = new Date(year, month, day);
  
  return localDate.toLocaleDateString(locale);
}

/**
 * Formats a league day date with custom options.
 * Always displays the intended date regardless of the user's local timezone.
 * 
 * @param date - Date object or ISO string
 * @param options - Intl.DateTimeFormat options
 * @param locale - Locale for formatting (default: 'en-GB')
 * @returns Formatted date string
 */
export function formatLeagueDayDateCustom(
  date: Date | string,
  options: Intl.DateTimeFormatOptions,
  locale: string = 'en-GB'
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  const year = dateObj.getUTCFullYear();
  const month = dateObj.getUTCMonth();
  const day = dateObj.getUTCDate();
  
  const localDate = new Date(year, month, day);
  
  return localDate.toLocaleDateString(locale, options);
}
